import { api } from './api-client';

const VAPID_PUBLIC_KEY =
  (import.meta.env.VITE_VAPID_PUBLIC_KEY as string | undefined) ?? '';

function urlBase64ToUint8Array(base64String: string): Uint8Array<ArrayBuffer> {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = atob(base64);
  const buffer = new ArrayBuffer(raw.length);
  const out = new Uint8Array(buffer);
  for (let i = 0; i < raw.length; i++) out[i] = raw.charCodeAt(i);
  return out;
}

export function pushSupported(): boolean {
  return (
    typeof window !== 'undefined' &&
    'serviceWorker' in navigator &&
    'PushManager' in window &&
    !!VAPID_PUBLIC_KEY
  );
}

export async function getCurrentSubscription(): Promise<PushSubscription | null> {
  if (!pushSupported()) return null;
  const reg = await navigator.serviceWorker.ready;
  return reg.pushManager.getSubscription();
}

export async function subscribeToPush(): Promise<PushSubscription> {
  if (!pushSupported()) {
    throw new Error('Web-Push wird auf diesem Gerät nicht unterstützt');
  }
  const permission = await Notification.requestPermission();
  if (permission !== 'granted') {
    const hint =
      permission === 'denied'
        ? 'Browser/iOS hat die Berechtigung blockiert (denied). PWA in den iOS-Einstellungen oder per Neuinstallation zuruecksetzen.'
        : permission === 'default'
          ? 'Der Dialog wurde nicht beantwortet (default). Bitte erneut tippen und "Erlauben" waehlen.'
          : `Unbekannter Status: ${permission}`;
    throw new Error(`Push-Erlaubnis nicht erteilt — ${hint}`);
  }
  const reg = await navigator.serviceWorker.ready;
  let subscription = await reg.pushManager.getSubscription();
  if (!subscription) {
    subscription = await reg.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(VAPID_PUBLIC_KEY),
    });
  }
  const json = subscription.toJSON();
  await api.post('/push/subscribe', {
    endpoint: json.endpoint,
    keys: json.keys,
    userAgent: navigator.userAgent,
  });
  return subscription;
}

export async function unsubscribeFromPush(): Promise<void> {
  const sub = await getCurrentSubscription();
  if (!sub) return;
  await api.delete('/push/subscribe', { endpoint: sub.endpoint });
  await sub.unsubscribe();
}

export interface PushDiagnostics {
  permission: NotificationPermission | 'unsupported';
  pushSupported: boolean;
  isPwa: boolean;
  swState: string;
  hasExistingSubscription: boolean;
  vapidConfigured: boolean;
}

/**
 * Sammelt alles was fuer das Debuggen von Push-Problemen nuetzlich ist.
 * Wird in der Settings-Seite gezeigt, wenn der Subscribe-Versuch fehlschlaegt.
 */
export async function getPushDiagnostics(): Promise<PushDiagnostics> {
  const isPwa =
    typeof window !== 'undefined' &&
    (window.matchMedia('(display-mode: standalone)').matches ||
      (window.navigator as unknown as { standalone?: boolean }).standalone ===
        true);
  const permission: NotificationPermission | 'unsupported' =
    typeof Notification === 'undefined' ? 'unsupported' : Notification.permission;

  let swState = 'unknown';
  let hasExistingSubscription = false;
  if (typeof navigator !== 'undefined' && 'serviceWorker' in navigator) {
    try {
      const reg = await navigator.serviceWorker.ready;
      swState = reg.active?.state ?? 'no-active';
      const sub = await reg.pushManager.getSubscription();
      hasExistingSubscription = !!sub;
    } catch (e) {
      swState = `error: ${(e as Error).message}`;
    }
  } else {
    swState = 'no-sw-api';
  }

  return {
    permission,
    pushSupported: pushSupported(),
    isPwa,
    swState,
    hasExistingSubscription,
    vapidConfigured: !!VAPID_PUBLIC_KEY,
  };
}
